import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import EditIcon from "@mui/icons-material/Edit";
import { Box, Card, Divider, IconButton, Stack, Typography } from "@mui/material";
import { debtIndexNames } from "constants/debt";
import { useFormatDateHook } from "hooks/shared-hooks/use-format-date-hook";
import { useDealDefaultCurrencySelect } from "store/selectors/deal-selectors/deal-selectors";
import { AmortizationData, DebtSegment } from "store/types";
import { formatNumber } from "utils";
import { getFacilitySegment, getLastSegmentsByDealId } from "utils/debt";

import { currencySign } from "../../constants";

type Props = {
  data: AmortizationData;
  isFacility: boolean;
  onEditClick: (event: React.MouseEvent<HTMLElement>) => void;
  scrollContainerRef?: React.RefObject<HTMLElement>;
};

const PAYMENT_TERM_DISPLAY: Record<string, string> = {
  fixed_principal: "Fixed Principal",
  fixed_total: "Fixed Total",
  custom: "Custom",
  balloon: "Balloon",
  partial_balloon: "Partial Balloon"
};

const FREQUENCY_DISPLAY: Record<string, string> = {
  month: "Monthly",
  quarter: "Quarterly",
  "semi-annual": "Semi-Annual",
  year: "Annual"
};

type PropertyProps = { label: string; value: string };

const Property: React.FC<PropertyProps> = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      sx={{ fontSize: "10px", fontWeight: 600, color: "text.secondary", textTransform: "uppercase", lineHeight: 1.2 }}
    >
      {label}
    </Typography>
    <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "text.primary", mt: 0.4, whiteSpace: "nowrap" }}>
      {value}
    </Typography>
  </Box>
);

const CompactProperty: React.FC<PropertyProps> = ({ label, value }) => (
  <Stack direction="row" alignItems="baseline" spacing={0.5}>
    <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "text.secondary", textTransform: "uppercase" }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "text.primary", whiteSpace: "nowrap" }}>
      {value}
    </Typography>
  </Stack>
);

export const AmortizationSummaryCard: React.FC<Props> = ({ data, isFacility, onEditClick, scrollContainerRef }) => {
  const { t } = useTranslation("debt");
  const format = useFormatDateHook();
  const currency = useDealDefaultCurrencySelect();
  const cardRef = useRef<HTMLDivElement>(null);
  const [isStuck, setIsStuck] = useState(false);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => setIsStuck(entry.intersectionRatio < 1), {
      root: scrollContainerRef?.current ?? null,
      threshold: [1],
      rootMargin: "-1px 0px 0px 0px"
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [scrollContainerRef]);

  const reportRow = data.table.rows.find((r) => r.is_report);
  const lastSegments = getLastSegmentsByDealId(data.segments);
  const facilitySegment = getFacilitySegment(lastSegments);
  const refSegment: DebtSegment | undefined = facilitySegment ?? lastSegments[0];
  const sign = currencySign[currency!] ?? "$";

  const translateIndex = (value: string | null): string => {
    if (!value) return "";
    const key = debtIndexNames[value];
    return key ? t(key, { defaultValue: key }) : value;
  };

  const formatRate = (
    baseRate: number | null | undefined,
    floatType: string | null | undefined,
    refIndex: string | null | undefined
  ): string => {
    if (baseRate == null) return "Off";
    const rateStr = `${formatNumber(baseRate)}%`;
    if (floatType === "spread" && refIndex) {
      const indexDisplay = translateIndex(refIndex);
      return indexDisplay ? `${indexDisplay} + ${rateStr}` : rateStr;
    }
    return rateStr;
  };

  const outstanding = reportRow != null ? `${sign}${formatNumber(reportRow.actual_principal)}` : "—";
  const commitment = facilitySegment != null ? `${sign}${formatNumber(facilitySegment.loan_amount)}` : "—";
  const disbursements = reportRow?.sub_deal ?? "—";

  const paymentTerm = refSegment ? (PAYMENT_TERM_DISPLAY[refSegment.payment_term] ?? refSegment.payment_term) : "—";
  const maturity = refSegment ? format({ date: refSegment.maturity_date }) : "—";
  const frequency = refSegment?.principal_frequency
    ? (FREQUENCY_DISPLAY[refSegment.principal_frequency] ?? refSegment.principal_frequency)
    : "—";
  const rate = refSegment
    ? formatRate(refSegment.base_rate, refSegment.float_type, refSegment.interest_reference_index)
    : "—";
  const pikRate = refSegment
    ? formatRate(refSegment.pik_base_rate, refSegment.pik_float_type, refSegment.pik_reference_index)
    : "—";

  return (
    <Card
      ref={cardRef}
      elevation={isStuck ? 3 : 1}
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 10,
        px: isStuck ? 3 : 5,
        py: isStuck ? 1.5 : 3,
        overflow: "visible",
        transition: "padding 0.15s ease, box-shadow 0.15s ease"
      }}
    >
      {isStuck ? (
        <Stack direction="row" alignItems="center" spacing={3} flexWrap="wrap">
          <CompactProperty label="Outstanding" value={outstanding} />
          {isFacility && (
            <>
              <Divider orientation="vertical" flexItem />
              <CompactProperty label="Disbursements" value={disbursements} />
            </>
          )}
          <Divider orientation="vertical" flexItem />
          <CompactProperty label="Rate" value={rate} />
          <Divider orientation="vertical" flexItem />
          <CompactProperty label="Maturity" value={maturity} />
          <Box sx={{ ml: "auto" }}>
            <IconButton size="sm" onClick={onEditClick}>
              <EditIcon fontSize="sm" />
            </IconButton>
          </Box>
        </Stack>
      ) : (
        <Stack direction="row" alignItems="flex-start" spacing={4}>
          {/* Left group */}
          <Stack direction="row" spacing={4} flexShrink={0}>
            {isFacility && <Property label="Commitment" value={commitment} />}
            <Property label="Outstanding" value={outstanding} />
            {isFacility && <Property label="Disbursements" value={disbursements} />}
          </Stack>

          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

          {/* Right group */}
          <Stack direction="row" spacing={4} flex={1} flexWrap="wrap" rowGap={2}>
            <Property label="Payment Terms" value={paymentTerm} />
            <Property label="Maturity" value={maturity} />
            <Property label="Principal Frequency" value={frequency} />
            <Property label="Rate" value={rate} />
            <Property label="PIK Interest" value={pikRate} />
          </Stack>

          <Box sx={{ ml: "auto", flexShrink: 0 }}>
            <IconButton onClick={onEditClick}>
              <EditIcon />
            </IconButton>
          </Box>
        </Stack>
      )}
    </Card>
  );
};
